/**
 * Money to the tetri, rounded the way the DATABASE rounds it (1.53).
 *
 * ⚠️ Why this file exists. sales.total, discount and paid are numeric(10,2):
 * Postgres rounds the decimal text supabase-js sends („4.725", the same text
 * `String(n)` gives) half away from zero, so a ₾5.25 sale less 10% was stored
 * as ₾4.73. The till said „4.72": JavaScript's toFixed(2) rounds the BINARY
 * number, and 4.725 is 4.72499… in binary. The screen, the receipt and the
 * change were a tetri away from the stored sale and from what the shift close
 * counts (about 0.3% of discounted or weighed sales; the owner, 2026-10-17:
 * „ეგეც გავასწოროთ აუცილებლად").
 *
 * So the rounding is done on the decimal TEXT's digits — the number the server
 * will see, rounded as the server will round it, on its first dropped digit
 * (re-parsing „1.7249999999999999e2" as a float would give 172.5 and round
 * the wrong way; final review I1). An amount already in whole tetri comes out
 * exactly as before.
 */

/** The decimal text shifted two places: „4.725" → 472.5, „1e-7" → 0.00001. */
function shifted(abs: number): number {
  const [mantissa, exp = '0'] = String(abs).split('e')
  return Number(`${mantissa}e${Number(exp) + 2}`)
}

/** ₾ → whole tetri, half away from zero on the decimal digits, as Postgres numeric rounds. */
export function toTetri(n: number): number {
  if (!Number.isFinite(n) || n === 0) return 0
  const sign = n < 0 ? -1 : 1
  const [mantissa, exp = '0'] = String(Math.abs(n)).split('e')
  const [whole, frac = ''] = mantissa.split('.')
  const digits = whole + frac
  const point = whole.length + Number(exp) + 2          // where the point falls in tetri
  if (point <= 0) return sign * (point === 0 && digits[0] >= '5' ? 1 : 0)
  if (point >= digits.length) return sign * Number(digits.padEnd(point, '0'))
  const kept = Number(digits.slice(0, point))
  return sign * (digits[point] >= '5' ? kept + 1 : kept)
}

/** ₾ rounded to the tetri, as the database will store it. */
export const roundMoney = (n: number): number => toTetri(n) / 100

/** A number's decimal text as whole digits and a scale: „1.005" → 1005, 3. */
function decimalParts(abs: number): { digits: number; scale: number } {
  const [mantissa, exp = '0'] = String(abs).split('e')
  const [whole, frac = ''] = mantissa.split('.')
  const scale = frac.length - Number(exp)
  const digits = Number(whole + frac)
  return scale >= 0 ? { digits, scale } : { digits: digits * 10 ** -scale, scale: 0 }
}

/**
 * a × b in whole tetri, as the server's round(a::numeric * b::numeric, 2): the
 * EXACT product of the two decimal texts, half away from zero. The float
 * product rounds first (1 × 1.005 = 1.00499… → ₾1.00; numeric says ₾1.01).
 */
export function mulTetri(a: number, b: number): number {
  if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0 || b === 0) return 0
  const sign = (a < 0) !== (b < 0) ? -1 : 1
  const x = decimalParts(Math.abs(a))
  const y = decimalParts(Math.abs(b))
  const product = x.digits * y.digits
  const scale = x.scale + y.scale
  if (!Number.isSafeInteger(product)) return toTetri(a * b)   // beyond any till's figures
  if (scale <= 2) return sign * product * 10 ** (2 - scale)
  const div = 10 ** (scale - 2)
  const rest = product % div
  const kept = (product - rest) / div
  return sign * (rest * 2 >= div ? kept + 1 : kept)
}

/** ₾ cut down to the tetri (toward zero) — never more than the amount. */
export function floorMoney(n: number): number {
  if (!Number.isFinite(n) || n === 0) return 0
  const sign = n < 0 ? -1 : 1
  return (sign * Math.floor(shifted(Math.abs(n)) + 1e-9)) / 100
}

/**
 * The cart's discount as the database will keep it. A percent is cut DOWN to
 * the tetri, so it never comes out above the percent typed (a cashier's
 * ceiling is a percent); an amount is taken to the tetri. Never above the lines.
 */
export function cartDiscount(subtotal: number, type: 'percent' | 'amount', raw: number): number {
  const typed = Number.isFinite(raw) && raw > 0 ? raw : 0
  if (type === 'percent') return floorMoney(Math.min(subtotal, (subtotal * typed) / 100))
  return roundMoney(Math.min(subtotal, typed))
}

/**
 * A line as sale_items keeps it (total_price is numeric(10,2)): to the tetri,
 * so the receipt's lines add up to its total and to the database. A PROMOTED
 * line stays as computed — create_sale checks a promotion's floor on the sum
 * of its lines (−0.01), and each line rounded down could pass it on 3+ lines.
 */
export const lineAmount = (raw: number, promoted: boolean): number => (promoted ? raw : roundMoney(raw))

/**
 * The sale's total — what the till shows, prints, takes and sends, and the
 * database keeps — built in whole tetri, so the subtotal, the discount and the
 * bonus shown add up to it (final review M2). create_sale accepts it: its lines
 * minus the discount and bonus are within 0.005 of it (tolerance 0.01).
 */
export const cartTotal = (subtotal: number, discount: number, bonus: number): number =>
  Math.max(0, toTetri(subtotal) - toTetri(discount) - toTetri(bonus)) / 100
